"use client"

import * as React from "react"

import { cn, Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@ibada/ui"

type MobileFormSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** A short note under the form (what can and cannot change), never a paragraph above the fields. */
  note?: React.ReactNode
  cancelLabel: string
  saveLabel: string
  onSave: () => void
  saving?: boolean
  saveDisabled?: boolean
  /** The save pill in red, for a confirm that removes or refuses something (reject, exclude). */
  destructive?: boolean
  /** `MobileFormSection`s with the kit's rows. */
  children: React.ReactNode
}

/**
 * A small edit on a phone (a few fields, not a whole screen): the app's bottom sheet with the kit's grouped rows,
 * and Annuler / Enregistrer as two pills at the bottom. For a full create form, use `MobileFormScreen`.
 */
export function MobileFormSheet({
  open,
  onOpenChange,
  title,
  note,
  cancelLabel,
  saveLabel,
  onSave,
  saving = false,
  saveDisabled = false,
  destructive = false,
  children,
}: MobileFormSheetProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className='max-h-[92dvh]'>
        <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
          <DrawerTitle className='truncate text-lg font-extrabold'>{title}</DrawerTitle>
          <DrawerDescription className='sr-only'>{title}</DrawerDescription>
        </DrawerHeader>
        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-3 [&>*]:shrink-0'>
          {children}
          {note ? <p className='px-1 text-xs font-semibold text-muted-foreground'>{note}</p> : null}
        </div>
        <div className='grid grid-cols-2 gap-2.5 border-t border-border/60 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]'>
          <button
            type='button'
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className='h-[3.25rem] rounded-full bg-muted text-base font-extrabold text-foreground active:bg-muted disabled:opacity-60'
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            onClick={onSave}
            disabled={saving || saveDisabled}
            className={cn(
              "flex h-[3.25rem] items-center justify-center rounded-full text-base font-extrabold active:brightness-95 disabled:opacity-45",
              destructive ? "bg-destructive text-white" : "bg-primary text-primary-foreground",
            )}
          >
            {saving ? <span className='size-5 animate-spin rounded-full border-2 border-current border-t-transparent' /> : saveLabel}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
